import type { LoadingStatus } from 'components/users/flexlist/FlexList';
import { schema } from 'normalizr';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule, xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { Duration } from 'types/units';
import type { Avatar } from 'types/user';
import { actionCreatorFactory } from 'typescript-fsa';

export const SUMMARIES_SCHEMA_KEY = 'summaries';
export const summariesSchema = new schema.Entity<RawUserSummary>(SUMMARIES_SCHEMA_KEY);
export const summariesListSchema = [summariesSchema];

export const summariesXhrModule = createXhrStatusModule('summaries');
const actionCreator = actionCreatorFactory('timely/summaries');

export const fetchedSummaries = actionCreator<{
  summaries: Array<RawUserSummary>;
  key: string;
  hasAccessToTasks: boolean;
  scope?: 'forecasts' | null;
}>('FETCHED_USER_SUMMARIES');

export const clearSummaries = actionCreator('CLEAR_SUMMARIES');

export type BaseSummaryDurations = {
  billable_duration: Duration;
  non_billable_duration: Duration;
  planned_duration: Duration;
  /** From tasks */
  tasked_duration: Duration;
  duration: Duration;
  total_duration: Duration;
};

export interface DaySummary extends BaseSummaryDurations {
  day: string;
  capacity: Duration;
}

export interface RawUserSummary extends BaseSummaryDurations {
  access_all_hours: boolean;
  avatar: Avatar;
  by_days: Array<DaySummary>;
  email: string;
  id: number;
  name: string;
  total_capacity: Duration;
  total_logged_percent: number;
  updated_at: string;
  weekly_capacity: Duration;
}

export interface GroupedSummaryDuration extends BaseSummaryDurations {
  capacity: Duration;
  since: string;
  until: string;
}

export interface RawUserSummaryGrouped extends RawUserSummary {
  grouped: Array<GroupedSummaryDuration>;
}

export type UserSummaryByDay = Pick<
  RawUserSummary,
  | 'weekly_capacity'
  | 'updated_at'
  | 'access_all_hours'
  | 'billable_duration'
  | 'non_billable_duration'
  | 'duration'
  | 'total_duration'
  | 'by_days'
  | 'total_capacity'
  | 'total_logged_percent'
> | null;

export interface UserSummary {
  avatar: Avatar;
  email: string;
  id: number;
  name: string;
  byKey: Record<string, UserSummaryByDay>;
  status: LoadingStatus;
}

export interface State {
  entities: Record<number, UserSummary>;
  result: Array<number>;
  xhrStatus: XhrStatus;
}

export const initialState: State = {
  entities: {},
  result: [],
  xhrStatus: xhrStatusFactory(),
};
